// Plain words for a trigger: the JS twin of backend/api/agents/triggers.py describe().
//
// It says the same words as describe() for every trigger validate() accepts, and for a stored row
// nobody validated: a timed trigger that isn't well formed reads as UNREADABLE, never as a
// half-filled sentence, and no raw stored text is ever printed. To get there it reads a row through
// ports of the readers describe() uses (hm, day, weekdays, setting), quirks included.
//
// triggerText.cases.json is the table that keeps the twins together: the backend test
// (backend/api/tests/test_agents_trigger_text.py) checks it against describe(), and
// triggerText.test.js against describeTrigger(). Change describe(), this file and the table
// together; regenerate the table with `cd backend && python api/tests/make_trigger_text_cases.py`.
//
// Known divergence: JSON can't tell 4.0 from 4. The backend reads a stored 4.0 (a float) as not a
// whole number, and here it is the number 4. Only a whole-number float differs, validate() never
// stores one, and the table leaves such values out.
export const EVENTS = [
  ['new_fax', 'When a new fax arrives'],
  ['new_report', 'When a report is filed to a chart'],
  ['visit_soon', 'Before a visit starts'],
  ['confirmation', 'When a patient answers the reminder text'],
  ['note_signed', 'When a note is signed'],
  ['task_assigned', 'When a task is assigned to you'],
  ['new_message', 'When a message arrives in your channels'],
];
// A Map, not an object: an event called "constructor" or "toString" is not a label.
const EVENT_LABELS = new Map(EVENTS);
export const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];
export const UNREADABLE = "Schedule can't be read";
const MAX_LIST_ENTRIES = 64;
const MAX_SETTING = 10 ** 9;

// What Python's str.strip() takes off the ends of a text: the code points str.isspace() is true
// for. JS trim() is not the same set: it also takes U+FEFF, and leaves 0x1c to 0x1f and 0x85.
const PY_SPACE = [
  [0x09, 0x0d], [0x1c, 0x20], [0x85, 0x85], [0xa0, 0xa0], [0x1680, 0x1680], [0x2000, 0x200a],
  [0x2028, 0x2029], [0x202f, 0x202f], [0x205f, 0x205f], [0x3000, 0x3000],
];
const isPySpace = (unit) => PY_SPACE.some(([low, high]) => unit >= low && unit <= high);
function strip(text) {
  let start = 0;
  let end = text.length;
  while (start < end && isPySpace(text.charCodeAt(start))) start += 1;
  while (end > start && isPySpace(text.charCodeAt(end - 1))) end -= 1;
  return text.slice(start, end);
}

// The backend's hm(): "HH:MM" or "H:MM" on the 24-hour clock, answered as "HH:MM"; else null.
// No "24:00", no seconds, ASCII digits only.
const HM = /^([0-9]{1,2}):([0-9]{2})$/;
function hm(text) {
  if (typeof text !== 'string') return null;
  const match = HM.exec(strip(text));
  if (!match || Number(match[1]) > 23 || Number(match[2]) > 59) return null;
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

// The backend's _day(): "YYYY-MM-DD" naming a day of the calendar (years 1 to 9999), answered as
// that same text; else null.
const DAY = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/;
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const isLeapYear = (year) => year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
function day(text) {
  if (typeof text !== 'string') return null;
  const clean = strip(text);
  const match = DAY.exec(clean);
  if (!match) return null;
  const [year, month, dayOfMonth] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (year < 1 || month < 1 || month > 12) return null;
  const last = month === 2 && isLeapYear(year) ? 29 : DAYS_IN_MONTH[month - 1];
  return dayOfMonth >= 1 && dayOfMonth <= last ? clean : null;
}

// The backend's _setting(): a stored setting as a whole number from 1 to a billion, else null. A
// string, a boolean, zero, a negative or an absurd size is a row nobody validated: it is not read,
// and not printed. (Its _whole(), a JSON integer that isn't a boolean, is Number.isInteger here:
// false for a boolean, a string and the rest. weekdays() reads its entries the same way.)
function setting(value) {
  return Number.isInteger(value) && value >= 1 && value <= MAX_SETTING ? value : null;
}

// The backend's _weekdays(): a days list as sorted, de-duplicated weekday numbers (Monday 0 to
// Sunday 6), every day when none are named; null when `value` isn't a list of weekday numbers or
// has more than 64 entries (the length is looked at first).
function weekdays(value) {
  if (value == null) return [...ALL_DAYS];
  if (!Array.isArray(value) || value.length > MAX_LIST_ENTRIES) return null;
  if (!value.every((d) => Number.isInteger(d) && d >= 0 && d <= 6)) return null;
  const days = [...new Set(value)].sort((a, b) => a - b);
  return days.length ? days : [...ALL_DAYS];
}

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

export function describeTrigger(trigger) {
  const t = isObject(trigger) ? trigger : {};
  if (t.kind === 'once') {
    const date = day(t.date);
    const time = hm(t.time);
    return date === null || time === null ? UNREADABLE : `Once, ${date} at ${time}`;
  }
  if (t.kind === 'repeat') {
    // every_hours wins over the time and days, whatever it holds. Only null (or no key) is "not set".
    if (t.every_hours != null) {
      const hours = setting(t.every_hours);
      return hours === null ? UNREADABLE : `Every ${hours} h`;
    }
    const time = hm(t.time);
    const days = weekdays(t.days);
    if (time === null || days === null) return UNREADABLE;
    let when = days.map((d) => DAY_NAMES[d]).join(', ');
    if (days.length === 7) when = 'Daily';
    else if (days.join(',') === '0,1,2,3,4') when = 'Weekdays';
    return `${when} at ${time}`;
  }
  if (t.kind === 'event') {
    return (typeof t.event === 'string' && EVENT_LABELS.get(t.event)) || 'On an event';
  }
  if (t.kind === 'watch') {
    const every = setting(t.every_minutes);
    const days = setting(t.days);
    return every === null || days === null ? UNREADABLE : `Every ${every} min, for ${days} days`;
  }
  return 'Now';
}

// What an agent wears in a list: its status when it takes no runs, else its schedule. The table's
// check refuses "archived" today, but the backend names it (NO_NEW_RUNS, the views' HALTED).
const STATUS_LABELS = new Map([
  ['paused', 'Paused'],
  ['done', 'Done'],
  ['suspended', 'Suspended'],
  ['archived', 'Archived'],
]);
export function statusLabel(agent) {
  return STATUS_LABELS.get(agent?.status) || describeTrigger(agent?.trigger);
}
